import type { CommonEventTriggerEventProps, PurchaseSource } from '@sunflower-games/analytics-events';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import compact from 'lodash/compact';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import { action, autorun, comparer, computed, makeObservable, observable, reaction, when } from 'mobx';

import { PACK_FLY_ADVANCE_CAP_MS } from '../../components/EventTriggerDialog/gifts/GiftLayout/components/RipsPackClaimFly/constants';
import { type RipsPackClaimFlyRequest } from '../../components/EventTriggerDialog/gifts/GiftLayout/components/RipsPackClaimFly/useRipsPackClaimFly';
import type { StoreOfferType } from '../../components/StoreOffers/types';
import {
  SOCKET_CLAIM_PACKAGE_COMPLETED,
  SOCKET_EVENT_STARTED,
  SOCKET_USER_EVENT_TRIGGER,
} from '../../config/constants';
import { analytics } from '../../services/analytics';
import type { EventAction, EventFeatureSubType, WidgetEventOptionalArgs } from '../../services/analytics/types';
import { postRequest } from '../../services/api';
import { logToNewRelic } from '../../services/debug';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import {
  isActionEventTrigger,
  isCoinBackBoosterEventTrigger,
  getBenefitTypeAnalyticsProps,
  getGiftPackageCountAnalyticsProps,
  isGiftPopupType,
  isMetagameCtaMessage,
  isOfferPopupType,
  isOfferV2PopupType,
  isPersonalGiftPopupType,
  isQuestEventTrigger,
  isRaffleBenefit,
  isRollingOfferPopupType,
  isTakeOrLoseBenefit,
  isWithinRewardClaimWindow,
} from '../../utils/events';
import { sleep } from '../../utils/helpers';
import { getPlayerLimitedOfferStock } from '../../utils/limitedOffer';
import { devError, devLog } from '../../utils/log';
import { singleFlight } from '../../utils/singleFlight';
import { formatTime } from '../../utils/time';
import type { FeatureFetchStatus, FeatureManagerDialogType, IFeatureManagerStore } from '../feature-manager/types';
import type { BundleItem } from '../rewards/types';
import { type RootStore } from '../RootStore';
import { Loadable } from '../traits/Loadable';
import { EnumOpenDialog } from '../ui/types';

import { isAutoPopupSuppressed, suppressAutoPopupIfNeeded } from './autoPopupSuppression';
import {
  GRANTED_EXTRA_OFFER_OPEN_WINDOW_MS,
  GRANTED_OFFER_LOOKUP_ATTEMPTS,
  GRANTED_OFFER_LOOKUP_DELAY_MS,
  GRANTED_OFFER_SOCKET_HANDLED_MS,
  POPUP_ANIMATIOM_DURATION_SECS,
  RAFFLE_STAGE_TO_EVENT_NAME,
  WIDGET_SAFE_ID_PREFIX,
} from './constants';
import { reduceInvalidEventsData, reduceInvalidPersonalGifts } from './eventTriggerSchemaValidation';
import {
  type Benefit,
  type ClaimPackageCompletedEnvelope,
  EVENT_TRIGGER_ACTION_TYPE,
  type EventTriggerPopupData,
  type EventTriggerPopupType,
  type EventTriggersResponse,
  type OfferPackage,
  type OfferV2PopupType,
  type RoadUserState,
  type RollingOfferPopupType,
} from './types';

type StoreCompatibleBenefit = OfferV2PopupType | RollingOfferPopupType;

// Missing/invalid start dates sort first, so a dated grant always wins and ties keep list order.
const startTime = (event: EventTriggerPopupData) => new Date(event.eventTrigger.startDate ?? 0).getTime() || 0;

export class EventTriggerStore extends Loadable implements IFeatureManagerStore {
  private readonly rootStore: RootStore;

  // Track events that have already been shown to prevent re-triggering
  private shownEventIds = new Set<string>();

  private grantedExtraOfferTriggerId = '';

  private grantedExtraOfferArmedAt = 0;

  // Granted offers already opened by either path (benefit key or CLAIM_PACKAGE_COMPLETED), so neither reopens them.
  private openedGrantedTriggerIds = new Set<string>();

  // When CLAIM_PACKAGE_COMPLETED opened each granted offer, so a benefit-key claim for the same grant returns at once.
  private socketOpenedGrantAt = new Map<string, number>();

  private benefitKeyLookupSeq = 0;

  private latestBenefitKeyLookup = new Map<string, number>();

  // Newest grant each benefit-key lookup is currently opening (it may be waiting on the offer's timer).
  private openingGrantByKey = new Map<string, string>();

  private cleanupTimers() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = undefined;
    }
    if (this.activeBenefitPackagesTimerInterval) {
      clearInterval(this.activeBenefitPackagesTimerInterval);
      this.activeBenefitPackagesTimerInterval = undefined;
    }
  }

  @action
  public reset() {
    this.widgetEventsLists = [];
    this.activeEventsPopupsQueue = [];
    this.currentActiveEventPopup = null;
    this.lastEventTriggerResponse = null;
    this.storeOffers = [];
    this.timers = {};
    this.activeBenefitPackagesTimers = {};
    this.shownEventIds.clear();
    this.clearGrantedExtraOffer();
    this.openedGrantedTriggerIds.clear();
    this.socketOpenedGrantAt.clear();
    this.latestBenefitKeyLookup.clear();
    this.openingGrantByKey.clear();
    this.isPopupOpen = false;
    this.isPopupCloseAnimationRunning = false;
    this.isBusy = false;
    this.isActionBusy = false;
    this.isDailyBonusClosed = false;
    this.fetchCount = 0;
    this.onCloseListener = undefined;
    this.ripsPackClaimFlyRequest = null;
    this.setLoading(false);
    this.cleanupTimers();
  }

  constructor(rootStore: RootStore) {
    super();
    this.rootStore = rootStore;
    makeObservable(this);
    const subscription = this.subscribeToEventsReceivedSocket.bind(this);
    const claimPackageSubscription = this.subscribeToClaimPackage.bind(this);
    const eventStartedSubscription = this.subscribeToEventStartedSocket.bind(this);

    this.initializeFeatureFlags();
    this.initIframePostMessage();

    autorun(() => {
      if (this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized) {
        socket.on(SOCKET_USER_EVENT_TRIGGER, subscription);
        socket.on(SOCKET_CLAIM_PACKAGE_COMPLETED, claimPackageSubscription);
        socket.on(SOCKET_EVENT_STARTED, eventStartedSubscription);
      } else {
        socket.off(SOCKET_USER_EVENT_TRIGGER, subscription);
        socket.off(SOCKET_CLAIM_PACKAGE_COMPLETED, claimPackageSubscription);
        socket.off(SOCKET_EVENT_STARTED, eventStartedSubscription);
        // Clear user-scoped state so the next session doesn't inherit it
        this.reset();
      }
    });

    reaction(
      () => [
        this.isPopupOpen,
        this.isBusy,
        this.rootStore.uiStore.CoinsAnimationVisible,
        this.rootStore.animationStore.animationBlockScreenVisible,
        this.widgetEventsLists.length,
      ],
      () => {
        void this.openGrantedExtraOfferIfReady();
      },
    );
  }

  private initializeFeatureFlags(): void {
    this.offerAnimationFlag = resolveFlag('enable_offer_rive_animation', true);
    this.giftAnimationFlag = resolveFlag('enable_gift_rive_animation', true);
    this.messageAnimationFlag = resolveFlag('enable_message_rive_animation', true);

    ldClient?.on('ready', () => {
      if (!ldClient) return;

      const enableOfferRiveAnimation = !!ldClient.variation('enable_offer_rive_animation', true);
      this.updateOfferAnimationFlag(enableOfferRiveAnimation);

      const enableGiftRiveAnimation = !!ldClient.variation('enable_gift_rive_animation', true);
      this.updateGiftAnimationFlag(enableGiftRiveAnimation);

      const enableMessageRiveAnimation = !!ldClient.variation('enable_message_rive_animation', true);
      this.updateMessageAnimationFlag(enableMessageRiveAnimation);
    });

    ldClient?.on('change', (changes) => {
      if ('enable_offer_rive_animation' in changes) {
        const newValue = changes.enable_offer_rive_animation.current;
        this.updateOfferAnimationFlag(newValue);
      }

      if ('enable_gift_rive_animation' in changes) {
        const newValue = changes.enable_gift_rive_animation.current;
        this.updateGiftAnimationFlag(newValue);
      }

      if ('enable_message_rive_animation' in changes) {
        const newValue = changes.enable_message_rive_animation.current;
        this.updateMessageAnimationFlag(newValue);
      }
    });
  }

  public dialogTypes: FeatureManagerDialogType[] = [
    'EventTriggerGifts',
    'PersonalGift',
    'EventTriggerMessages',
    'EventTriggerOffers',
    'EventTriggerOffersV2',
    'EventTriggerRollingOffers',
    'EventTriggerRoad',
  ];

  public lastOfferSource: PurchaseSource = 'offer';
  public roadSource: PurchaseSource = 'road';

  public async startDialogs() {
    return this.showNextPopup();
  }

  private fetchCount = 0;
  private incrementFetchCount() {
    this.fetchCount++;
  }

  private initIframePostMessage() {
    window.addEventListener('message', (e) => {
      const message = e.data;
      const eventTriggerId = message?.payload?.eventTriggerId;
      // Open offer event from CrownSlot
      if (message?.type === 'open_monetization_event' && eventTriggerId) {
        void this.openPopupByEventTriggerId(eventTriggerId);
      }
    });
  }

  public notifyMonetizationClosed() {
    this.rootStore.gameStore.sendSlotsGameUiMessage({ type: 'close_monetization_event' });
  }

  private handleCoinBackBoosterEventTrigger({
    currentEventsByTrigger,
    otherActiveEvents,
  }: {
    currentEventsByTrigger: EventTriggerPopupData[];
    otherActiveEvents: EventTriggerPopupData[];
  }) {
    let newCurrentEventsByTrigger = [...currentEventsByTrigger];
    let newOtherActiveEvents = [...otherActiveEvents];

    const coinBackInCurrentEvents = currentEventsByTrigger.find(
      (event) => event.eventTrigger.type === 'coinBackBooster',
    );
    const coinBackBoosterInOther = otherActiveEvents.find((event) => event.eventTrigger.type === 'coinBackBooster');

    if (coinBackInCurrentEvents && isCoinBackBoosterEventTrigger(coinBackInCurrentEvents.benefit)) {
      this.rootStore.coinBackStore.updateCoinBackBooster(coinBackInCurrentEvents, true);
      newCurrentEventsByTrigger = currentEventsByTrigger.filter(
        (event) => event.eventTrigger.type !== 'coinBackBooster',
      );
    }

    if (coinBackBoosterInOther && isCoinBackBoosterEventTrigger(coinBackBoosterInOther.benefit)) {
      this.rootStore.coinBackStore.updateCoinBackBooster(coinBackBoosterInOther, false);
      newOtherActiveEvents = otherActiveEvents.filter((event) => event.eventTrigger.type !== 'coinBackBooster');
    }

    return { currentEventsByTrigger: newCurrentEventsByTrigger, otherActiveEvents: newOtherActiveEvents };
  }

  private handleActiveEventsPopups(data: EventTriggersResponse) {
    const dataAfterCoinBackBooster = this.handleCoinBackBoosterEventTrigger(data);

    const { currentEventsByTrigger, otherActiveEvents } = dataAfterCoinBackBooster;

    return { ...data, currentEventsByTrigger, otherActiveEvents };
  }

  private compactEventTriggerData(data: EventTriggersResponse): EventTriggersResponse {
    return {
      ...data,
      currentEventsByTrigger: compact(data.currentEventsByTrigger),
      otherActiveEvents: compact(data.otherActiveEvents),
      personalGifts: compact(data.personalGifts),
    };
  }

  async triggerEvent({ triggerKey, additionalParams }: { triggerKey: string; additionalParams?: Record<string, any> }) {
    const csEventStart = resolveFlag('client_side_event_start', true);

    const response = await postRequest<ApiResponse<EventTriggersResponse>>('/v2/event-trigger/events', {
      triggerKey,
      csEventStart,
      ...additionalParams,
    });

    const data = get(response, 'data.data', null);

    if (!isEmpty(data)) {
      const dataWithEventsCompacted = this.compactEventTriggerData(data);

      const activeEventsData = this.handleActiveEventsPopups(dataWithEventsCompacted);
      this.setActiveEventPopups(activeEventsData);

      void analytics.sendEvent('events_fetched', {
        keys: data.currentEventsByTrigger.map((event) => event.benefit?.key ?? event.benefitKey ?? 'unknown').join(','),
      });
      return data;
    }

    this.setLastEventTriggerResponse(null);
    return null;
  }

  async fetchWithPromoCode(promoCode: string) {
    const responseData = await this.triggerEvent({ triggerKey: 'AFTER_PROMO_CODE', additionalParams: { promoCode } });

    if (responseData) {
      // Ensure newly fetched events are displayed immediately, similar to socket flow
      // by adding the relevant dialog types to the sequence queue
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(this.dialogTypes, true);
    }
    return responseData;
  }

  public fetch = singleFlight(async (): Promise<FeatureFetchStatus> => {
    if (this.rootStore.authStore.userCoolDown) {
      return 'OFF';
    }

    const featureFlag = resolveFlag('liveops', true);
    if (!featureFlag) {
      return 'OFF';
    }

    try {
      const responseData = await this.triggerEvent({ triggerKey: 'AFTER_LOGIN' });
      if (!responseData) {
        return 'ERROR';
      }
      this.incrementFetchCount();
      return 'READY';
    } catch (e) {
      devLog('error fetch event triggers data');
      return 'ERROR';
    }
  });

  @observable
  public offerAnimationFlag: boolean = false;

  @observable
  public giftAnimationFlag: boolean = false;

  @observable
  public messageAnimationFlag: boolean = false;

  private timerInterval: NodeJS.Timeout | undefined = undefined;
  private activeBenefitPackagesTimerInterval: NodeJS.Timeout | undefined = undefined;

  @observable
  public currentActiveEventPopup: EventTriggerPopupData | null = null;

  @observable
  public isBusy = false;

  @observable
  public isPopupOpen = false;

  @observable
  public isPopupCloseAnimationRunning = false;

  @observable
  public activeEventsPopupsQueue: EventTriggerPopupData[] = [];

  @observable
  public widgetEventsLists: EventTriggerPopupData[] = [];

  @observable
  public timers: Record<string, number> = {};

  @observable
  public activeBenefitPackagesTimers: Record<string, number> = {};

  @observable
  public isDailyBonusClosed = false;

  @observable
  public isActionBusy = false;

  @observable
  public lastEventTriggerResponse: EventTriggersResponse | null = null;

  // Captured (imageUrl + measured sourceRect only — no timers/phase) by the claim dialog at claim
  // time, while the ripsPack gift's art is still on screen. Read by RipsPackClaimFlyHost, a
  // persistent sibling of <Dialogs/> that outlives this dialog's own mount — the dialog unmounts as
  // soon as this store clears uiStore.openDialog (see closePopup below), which would destroy any
  // component-local state/portal the celebration depended on if it lived inside the dialog itself.
  @observable
  public ripsPackClaimFlyRequest: RipsPackClaimFlyRequest | null = null;

  @action
  public requestRipsPackClaimFly(request: RipsPackClaimFlyRequest) {
    this.ripsPackClaimFlyRequest = request;
  }

  @action
  public clearRipsPackClaimFlyRequest() {
    this.ripsPackClaimFlyRequest = null;
  }

  // Gates the popup-ADVANCE step only (showNextPopup / the next queued dialog), never the visual
  // modal close — the claim dialog's onEnd calls this right after closePopup() resolves, so the
  // next popup in the feature-manager queue (which otherwise runs back-to-back, no gap) doesn't
  // open on top of the pack still flying to Crown Rips.
  //
  // Resolves as soon as RipsPackClaimFlyHost clears ripsPackClaimFlyRequest (real flight
  // completion, on any termination path — see RipsPackClaimFlyHost), or immediately if no request
  // was ever made (no ripsPack, or captureRipsPackFlySource bailed — e.g. reduced motion). The
  // `when(...)` timeout is a hard, unconditional backstop: a missed advance wedges every future
  // popup (see the CTA-popup lesson — a feature-manager sequence must always be released), so this
  // must never throw/hang regardless of why the flight didn't finish.
  public async awaitRipsPackClaimFlyDone(): Promise<void> {
    if (!this.ripsPackClaimFlyRequest) {
      return;
    }
    try {
      await when(() => this.ripsPackClaimFlyRequest === null, { timeout: PACK_FLY_ADVANCE_CAP_MS });
    } catch {
      // Timed out (or any other when() failure) — release the advance unconditionally regardless,
      // and clear the request so it can't outlive a host that isn't mounted on this page (safe: a
      // live host holds its own local flyState; clearing the store request twice is a no-op).
      this.clearRipsPackClaimFlyRequest();
    }
  }

  @action
  updateOfferAnimationFlag(flag: boolean) {
    this.offerAnimationFlag = flag;
  }

  @action
  updateGiftAnimationFlag(flag: boolean) {
    this.giftAnimationFlag = flag;
  }

  @action
  updateMessageAnimationFlag(flag: boolean) {
    this.messageAnimationFlag = flag;
  }

  @action
  public setLastEventTriggerResponse(response: EventTriggersResponse | null) {
    this.lastEventTriggerResponse = response;
  }

  public getEventDataByUserEventTriggerId(userEventTriggerId: string) {
    return [
      ...(this.lastEventTriggerResponse?.currentEventsByTrigger || []),
      ...(this.lastEventTriggerResponse?.otherActiveEvents || []),
    ].find((event) => event.userEventTriggerId === userEventTriggerId);
  }

  // Whether an expired event's timer may extend into the reward-claim window. Rolling offers only
  // earn the claim period once a previous step was paid (an unresolvable package counts as paid)
  // and the current step's delay isn't still counting down; every other event type extends as before.
  public shouldExtendToClaimPeriod(event: EventTriggerPopupData): boolean {
    if (event.benefit.benefitType !== 'rollingOffer') return true;
    const benefit = event.benefit as Benefit<'rollingOffer'>;
    const currentStep = event.currentStep;
    if (currentStep == null) return false;

    const currentStepPackage = benefit.benefitPackages?.find((benPackage) => benPackage.order === currentStep);
    if (!currentStepPackage) return false;
    // `timeLeft` on the package is a fetch-time snapshot; the live FE countdown for the step's delay
    // lives in activeBenefitPackagesTimers (keyed by package id, removed once it reaches 0).
    const remainingDelay = this.activeBenefitPackagesTimers[currentStepPackage.id] ?? 0;
    if (currentStepPackage.delay > 0 && remainingDelay > 0) return false;

    const previousSteps = benefit.benefitPackages?.filter((benPackage) => benPackage.order < currentStep) ?? [];
    return previousSteps.some((benPackage) => {
      const pkg = benefit.packages?.find((p) => p.packageId === benPackage.packageId);
      return !pkg || pkg.price > 0;
    });
  }

  private markEventAsInClaimPeriod(userEventTriggerId: string) {
    const arrays = [
      this.lastEventTriggerResponse?.currentEventsByTrigger,
      this.lastEventTriggerResponse?.otherActiveEvents,
    ];
    for (const arr of arrays) {
      const event = arr?.find((e) => e.userEventTriggerId === userEventTriggerId);
      if (event) {
        event.isInClaimPeriod = true;
        return;
      }
    }
  }

  @observable
  public storeOffers: StoreOfferType[] = [];

  @action
  public setStoreOffers(storeOffers: StoreOfferType[]) {
    this.storeOffers = storeOffers;
  }

  @action
  public updateStoreOffers(activeEventsData: EventTriggerPopupData[]) {
    const storeOffersFeatureFlag = resolveFlag('store_offers_feature_enable', true);
    if (!storeOffersFeatureFlag) {
      return;
    }

    const storeOffers = activeEventsData
      .map((event) => {
        const isOfferV2 = isOfferV2PopupType(event.benefit);
        const isRollingOffer = isRollingOfferPopupType(event.benefit);

        if ((!isOfferV2 && !isRollingOffer) || !event.benefit?.visibility?.store) {
          return null;
        }

        const benefit = event.benefit as StoreCompatibleBenefit;
        const { storeLayout, storeLayoutImageUrl, packages, key } = benefit;
        if (!storeLayout || !storeLayoutImageUrl) {
          return null;
        }

        const {
          canDisplay,
          eventTrigger: { eventTriggerId },
          userEventTriggerId,
        } = event;

        return {
          benefitKey: key,
          storeLayout,
          storeLayoutImageUrl,
          packages,
          userEventTriggerId,
          canDisplay,
          eventTriggerId,
        };
      })
      .filter((offer) => offer !== null) as StoreOfferType[];

    this.setStoreOffers(storeOffers);

    const storeOfferTimers = activeEventsData.reduce<Record<string, number>>((acc, event) => {
      const isOfferV2 = isOfferV2PopupType(event.benefit);
      const isRollingOffer = isRollingOfferPopupType(event.benefit);

      if ((!isOfferV2 && !isRollingOffer) || !event.benefit?.visibility?.store || !event.userEventTriggerId) {
        return acc;
      }

      return {
        ...acc,
        [event.userEventTriggerId]: dayjs(event.expiredAt).diff(dayjs(), 'second'),
      };
    }, {});

    if (!isEmpty(storeOfferTimers)) {
      this.setTimers({
        ...this.timers,
        ...storeOfferTimers,
      });
      this.startTimers();
    }
  }

  @action
  setIsBusy(newIsBusy: boolean) {
    this.isBusy = newIsBusy;
  }

  @action
  setCurrentActiveEvent(event: EventTriggerPopupData | null) {
    this.currentActiveEventPopup = event;
  }

  @action
  public updateCurrentEventRoadState(newRoadState: Partial<RoadUserState>, roadEventTriggerId?: string) {
    let current = this.currentActiveEventPopup;
    if (!current && roadEventTriggerId) {
      //if the current is not active because the user closed the popup, find the event in the queue based on the road event trigger id
      current = this.widgetEventsLists.find((event) => event.userEventTriggerId === roadEventTriggerId) ?? null;
    }
    if (!current || !current.roadState || current.benefit.benefitType !== 'road') {
      return;
    }

    const userEventTriggerId = current.userEventTriggerId;

    // Update the active popup state
    this.setCurrentActiveEvent({
      ...current,
      roadState: {
        ...current.roadState,
        ...newRoadState,
      },
    });

    // Mirror the update into the active events queue so it persists across close/reopen
    if (userEventTriggerId) {
      this.setActiveEventsPopupsQueue(
        this.activeEventsPopupsQueue.map((event) => {
          if (event.userEventTriggerId === userEventTriggerId) {
            const baseRoadState = event.roadState ?? current.roadState;
            const updatedRoadState = {
              ...baseRoadState,
              ...newRoadState,
            } as RoadUserState;
            return {
              ...event,
              roadState: updatedRoadState,
            };
          }
          return event;
        }),
      );

      // Mirror the update into widget events (if present there)
      this.updateWidgetEvents(
        this.widgetEventsLists.map((widget) => {
          if (widget.userEventTriggerId === userEventTriggerId) {
            const baseRoadState = widget.roadState ?? current.roadState;
            const updatedRoadState = {
              ...baseRoadState,
              ...newRoadState,
            } as RoadUserState;
            return {
              ...widget,
              roadState: updatedRoadState,
            };
          }
          return widget;
        }),
      );
    }
  }

  @action
  public setDailyBonusClosed(isClosed: boolean) {
    this.isDailyBonusClosed = isClosed;
  }

  @action
  public setPopupCloseAnimationRunning(isRunning: boolean) {
    this.isPopupCloseAnimationRunning = isRunning;
  }

  @action
  private setActiveEventsPopupsQueue(activeEventsData: EventTriggerPopupData[]) {
    this.activeEventsPopupsQueue = activeEventsData.filter((event) => event.benefit.visibility?.lobby !== false);
  }

  @action
  public setIsActionBusy(value: boolean) {
    this.isActionBusy = value;
  }

  @action
  private setTimers(timers: Record<string, number>) {
    this.timers = timers;
  }

  @action
  private setActiveBenefitPackagesTimers(timers: Record<string, number>) {
    this.activeBenefitPackagesTimers = timers;
  }

  @action
  public setWidgetEventsList(newWidgetEvents: EventTriggerPopupData[]) {
    this.widgetEventsLists = newWidgetEvents;
  }

  @action
  public async openPopupByUserEventTriggerId(userEventTriggerId: string, onClose?: () => void) {
    let popup = this.widgetEventsLists.find((event) => event.userEventTriggerId === userEventTriggerId);
    if (popup) {
      if (!popup.canDisplay) {
        const triggerId = popup.isNewEventType ? popup.userEventTriggerId : popup.eventTrigger.eventTriggerId;

        popup = await this.startEventTimer(triggerId);

        if (!popup) {
          devError('Error while starting event timer', { popup });
          return;
        }
      }

      void this.openPopup(popup, onClose);
    }
  }

  @action
  // shouldStillOpen is rechecked after the timer start, so a caller superseded meanwhile doesn't open a stale popup.
  public async openPopupByEventTriggerId(eventTriggerId: string, onClose?: () => void, shouldStillOpen = () => true) {
    let popup = this.widgetEventsLists.find((event) => event.eventTrigger.eventTriggerId === eventTriggerId);
    if (popup) {
      if (!popup.canDisplay) {
        const triggerId = popup.isNewEventType ? popup.userEventTriggerId : popup.eventTrigger.eventTriggerId;

        popup = await this.startEventTimer(triggerId);

        if (!popup) {
          devError('Error while starting event timer', { popup });
          return;
        }
        if (!shouldStillOpen()) return;
      }

      void this.openPopup(popup, onClose);
    }
  }

  @action
  public removeEvent(userEventTriggerId: string) {
    const updatedActiveEvents = [...this.activeEventsPopupsQueue].filter(
      (event) => event.userEventTriggerId !== userEventTriggerId,
    );
    this.setActiveEventsPopupsQueue(updatedActiveEvents);

    this.setStoreOffers(this.storeOffers.filter((storeOffer) => storeOffer.userEventTriggerId !== userEventTriggerId));

    const event = this.getEventDataByUserEventTriggerId(userEventTriggerId);

    const eventHasLimit = event && event.eventTrigger.usesPerUserLimit !== -1;
    const isMessageEvent = event?.eventTrigger.type === 'message';
    const shouldRemoveWidget = !isMessageEvent || event?.eventTrigger.purchaseCooldown || eventHasLimit;
    if (shouldRemoveWidget) {
      const updatedWidgetEvents = [...this.widgetEventsLists].filter(
        (widgetEvent) => widgetEvent.userEventTriggerId !== userEventTriggerId,
      );
      this.updateWidgetEvents(updatedWidgetEvents);
    }

    if (this.currentActiveEventPopup?.userEventTriggerId === userEventTriggerId) {
      this.setCurrentActiveEvent(null);
    }
  }

  public getEventTimerFormatted(key: string, t?: TFunction) {
    const timerSecs = this.timers[key];
    return timerSecs ? formatTime(timerSecs, 's', t) : '0';
  }

  public getBenefitPackageTimerFormatted(key: string, t?: TFunction) {
    const timerSecs = this.activeBenefitPackagesTimers[key];
    return timerSecs ? formatTime(timerSecs, 's', t) : null;
  }

  private startTimers() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.timerInterval = setInterval(() => {
      const timers = { ...this.timers };

      Object.keys(timers).forEach((key) => {
        timers[key] = timers[key] - 1;
        if (timers[key] <= 0) {
          const event = this.getEventDataByUserEventTriggerId(key);
          const canExtendToClaimPeriod =
            event?.rewardClaimEndDate &&
            dayjs().isBefore(dayjs(event.rewardClaimEndDate)) &&
            this.shouldExtendToClaimPeriod(event);
          if (canExtendToClaimPeriod) {
            timers[key] = dayjs(event.rewardClaimEndDate).diff(dayjs(), 'second');
            this.markEventAsInClaimPeriod(key);
            if (isRollingOfferPopupType(event.benefit) && !this.isPopupOpen && !this.isBusy) {
              this.shownEventIds.add(key);
              this.sendAnalyticsEvents({ event_action: 'impression', triggered_by: 'lobby' }, event);
              void this.openPopupByUserEventTriggerId(key);
            }
          } else {
            delete timers[key];
            this.removeEvent(key);
          }
        }
      });

      this.setTimers(timers);
    }, 1000);
  }

  startActivePackagesTimers() {
    if (this.activeBenefitPackagesTimerInterval) {
      clearInterval(this.activeBenefitPackagesTimerInterval);
    }
    this.activeBenefitPackagesTimerInterval = setInterval(() => {
      const timers = { ...this.activeBenefitPackagesTimers };

      Object.keys(timers).forEach((key) => {
        timers[key] = timers[key] - 1;
        if (timers[key] <= 0) {
          delete timers[key];
          this.removeEvent(key);
        }
      });

      this.setActiveBenefitPackagesTimers(timers);
    }, 1000);
  }

  async startStoreOfferTimer(eventTriggerId: string) {
    try {
      const event = this.storeOffers.find((storeOffer) => storeOffer.eventTriggerId === eventTriggerId);

      if (!event || event.canDisplay) {
        return;
      }

      const { userEventTriggerId } = event;

      const response = await postRequest<ApiResponse<{ expiredAt: Date | undefined }>>(
        `event-trigger/${eventTriggerId}/start-timer`,
        {
          userEventTriggerId,
        },
      );

      const { expiredAt } = response.data.data;

      if (expiredAt) {
        this.setStoreOffers(
          this.storeOffers.map((storeOffer) => {
            if (storeOffer.eventTriggerId === eventTriggerId) {
              return {
                ...storeOffer,
                canDisplay: true,
              };
            }
            return storeOffer;
          }),
        );
        this.setTimers({
          ...this.timers,
          [event.userEventTriggerId]: dayjs(expiredAt).diff(dayjs(), 'second'),
        });
      }
    } catch (error) {
      devError('Error while starting store offer timer');
    }
  }

  async startEventTimer(eventTriggerId: string): Promise<EventTriggerPopupData | undefined> {
    try {
      const event: EventTriggerPopupData | undefined =
        this.widgetEventsLists.find((eventItem) => eventItem.userEventTriggerId === eventTriggerId) ||
        this.widgetEventsLists.find((eventItem) => eventItem.eventTrigger.eventTriggerId === eventTriggerId);

      if (!event) {
        // When offer only on store
        return undefined;
      }

      const isNewEventType = event.isNewEventType ?? false;
      const { userEventTriggerId } = event;

      const response = await postRequest<ApiResponse<{ expiredAt: Date | undefined }>>(
        `event-trigger/${event.eventTrigger.eventTriggerId}/start-timer`,
        {
          userEventTriggerId,
        },
      );

      const { expiredAt } = response.data.data;

      if (typeof expiredAt === 'undefined') {
        return undefined;
      }

      const updatedEvent = {
        ...event,
        expiredAt,
        canDisplay: true,
      };

      this.setStoreOffers(
        this.storeOffers.map((storeOffer) => {
          if (storeOffer.eventTriggerId === eventTriggerId) {
            return {
              ...storeOffer,
              canDisplay: true,
            };
          }
          return storeOffer;
        }),
      );

      const newWidgetEvents = this.widgetEventsLists.map((widget) => {
        if (widget.userEventTriggerId === updatedEvent.userEventTriggerId) {
          return updatedEvent;
        }
        return widget;
      });

      this.setWidgetEventsList(newWidgetEvents);

      const isInActiveEvents = this.activeEventsPopupsQueue.some(
        (eventItem) => eventItem.userEventTriggerId === updatedEvent.userEventTriggerId,
      );

      if (isInActiveEvents) {
        const newActiveEventsPopupsQueue = this.activeEventsPopupsQueue.map((activeEvent) => {
          if (!isNewEventType && activeEvent.eventTrigger.eventTriggerId === eventTriggerId) {
            return updatedEvent;
          } else if (isNewEventType && activeEvent.userEventTriggerId === eventTriggerId) {
            return updatedEvent;
          }

          return activeEvent;
        });

        this.setStoreOffers(
          this.storeOffers.map((storeOffer) => {
            if (storeOffer.eventTriggerId === eventTriggerId) {
              return {
                ...storeOffer,
                canDisplay: true,
              };
            }
            return storeOffer;
          }),
        );

        this.setActiveEventsPopupsQueue(newActiveEventsPopupsQueue);
      }

      this.updateWidgetOnStartTimer(updatedEvent.userEventTriggerId, expiredAt);

      // Notify the slot game so it can start the offer widget countdown.
      this.rootStore.gameStore.sendSlotsGameUiMessage({
        type: 'event_trigger_timer_start',
        payload: {
          eventTriggerId: event.eventTrigger.eventTriggerId,
          expiredAt: new Date(expiredAt).getTime(), // epoch ms
        },
      });

      return updatedEvent;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('Error while starting event timer', errorMessage);
      logToNewRelic('Error starting event timer', errorMessage, 'error');
      return undefined;
    }
  }

  @action
  private setPopupIsOpen(isOpen: boolean) {
    this.isPopupOpen = isOpen;
  }

  public async claimFreePackage(activePackage: OfferPackage) {
    let isClaimed = false;
    if (this.isActionBusy || !this.currentActiveEventPopup) return;

    this.setIsActionBusy(true);

    try {
      if (!this.currentActiveEventPopup || !isRollingOfferPopupType(this.currentActiveEventPopup.benefit)) {
        return;
      }

      const pkg = this.currentActiveEventPopup?.benefit.packages.find((p) => p.packageId === activePackage.packageId);

      if (!pkg || pkg.price !== 0) {
        devError('Cannot claim package with price', activePackage);
        return;
      }

      const { userEventTriggerId, eventTrigger } = this.currentActiveEventPopup;
      const { eventTriggerId } = eventTrigger;

      const response = await postRequest<ApiResponse<{ success: boolean }>>(
        '/v2/offers/rolling-offers/claim-free-package',
        {
          activeEvent: this.currentActiveEventPopup,
          userEventTriggerId,
          eventTriggerId,
          packageId: activePackage.packageId,
        },
      );

      if (response.data.success) {
        isClaimed = true;
        await this.rootStore.featureManagerStore.fetchAndStartDialogs(this.dialogTypes);
      }
    } catch (error) {
      devError('Cannot claim free package', error);
    }

    this.setIsActionBusy(false);
    return isClaimed;
  }

  public async declineOffer() {
    if (this.isActionBusy || !this.currentActiveEventPopup) return false;

    this.setIsActionBusy(true);

    try {
      const { eventTrigger } = this.currentActiveEventPopup;
      const { eventTriggerId } = eventTrigger;

      const response = await postRequest<ApiResponse<{ success: boolean }>>('/v2/offers/decline', {
        eventTriggerId,
      });

      if (!response.data.success) return false;

      await this.rootStore.featureManagerStore.fetchAndStartDialogs(this.dialogTypes);
      return true;
    } catch (error) {
      devError('Cannot decline offer', error);
      return false;
    } finally {
      this.setIsActionBusy(false);
    }
  }

  public getNextPopup(): EventTriggerPopupData | undefined {
    const dialogType = this.rootStore.featureManagerStore.currentFeatureType;

    switch (dialogType) {
      case 'EventTriggerGifts':
        return this.getMaxWeightPopup(['gift', 'multiPackageGift']);
      case 'PersonalGift':
        return this.getMaxWeightPopup('personalGift');
      case 'EventTriggerMessages':
        return this.getMaxWeightPopup('message');
      case 'EventTriggerOffers':
        return this.getMaxWeightPopup('offer');
      case 'EventTriggerOffersV2':
        return this.getMaxWeightPopup('2-3-offer');
      case 'EventTriggerRollingOffers':
        return this.getMaxWeightPopup('rollingOffer');
      case 'EventTriggerRoad':
        return this.getMaxWeightPopup('road');
      default:
        return undefined;
    }
  }

  private isMetagameCtaMessageVisible(event: EventTriggerPopupData): boolean {
    const { benefit } = event;
    return !isMetagameCtaMessage(benefit) || this.rootStore.metagamesStore.isMetagameAvailable(benefit.ctaValue);
  }

  @computed({ equals: comparer.shallow })
  public get visibleWidgetEvents(): EventTriggerPopupData[] {
    return this.widgetEventsLists.filter((event) => this.isMetagameCtaMessageVisible(event));
  }

  private getMaxWeightPopup(type: EventTriggerPopupType | EventTriggerPopupType[]) {
    const types = Array.isArray(type) ? type : [type];
    if (type === 'personalGift') {
      return this.activeEventsPopupsQueue.find((event) => event.benefit.benefitType === type);
    }

    const currentCoinType = this.rootStore.coinTypeStore.coinType.toLowerCase();

    const filteredPopups = this.activeEventsPopupsQueue.filter(
      (event) =>
        types.includes(event.benefit.benefitType) &&
        (!event.eventTrigger.extra?.coinMode || ['all', currentCoinType].includes(event.eventTrigger.extra.coinMode)) &&
        !isAutoPopupSuppressed(event.userEventTriggerId) &&
        this.isMetagameCtaMessageVisible(event),
    );

    const priorityPopups = filteredPopups.filter((popup) =>
      sessionStorage.getItem(`eventTriggerRollingOffers:${popup.userEventTriggerId}:purchaseCompleted`),
    );

    if (priorityPopups.length > 0) {
      return priorityPopups[0];
    }

    const restPopups = filteredPopups.filter((popup) => !priorityPopups.includes(popup));

    return restPopups.reduce(
      (acc, curr) => (acc.eventTrigger.weight > curr.eventTrigger.weight ? acc : curr),
      restPopups[0],
    );
  }

  private setAnimationProperties() {
    if (this.currentActiveEventPopup) {
      const { userEventTriggerId } = this.currentActiveEventPopup;
      const widget = document.getElementById(`${WIDGET_SAFE_ID_PREFIX}${userEventTriggerId}`);
      let rect = null;

      if (widget) {
        rect = widget?.getBoundingClientRect();
      } else {
        rect = document.body.getBoundingClientRect();
      }

      if (rect) {
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        document.documentElement.style.setProperty('--folding-duration', `${POPUP_ANIMATIOM_DURATION_SECS}s`);
        document.documentElement.style.setProperty('--widget-x', `${centerX}px`);
        document.documentElement.style.setProperty('--widget-y', `${centerY}px`);
      }
    }
  }

  public async setPopupState(args: { newIsOpen: boolean }) {
    if (this.isPopupCloseAnimationRunning) return;
    if (args.newIsOpen === this.isPopupOpen) return;

    this.setAnimationProperties();

    if (args.newIsOpen) {
      this.setPopupIsOpen(true);
      return;
    }

    this.setPopupCloseAnimationRunning(true);

    await sleep(POPUP_ANIMATIOM_DURATION_SECS * 1000);

    this.setPopupIsOpen(false);
    this.setPopupCloseAnimationRunning(false);
    this.setIsBusy(false);
  }

  public setEventToFirstInQueue(userEventTriggerId: string) {
    const activePopupEvent = this.activeEventsPopupsQueue.find(
      (event) => event.userEventTriggerId === userEventTriggerId,
    );
    if (!activePopupEvent) return;

    const filteredQueue = this.activeEventsPopupsQueue.filter(
      (event) => event.userEventTriggerId !== userEventTriggerId,
    );

    this.setActiveEventsPopupsQueue([activePopupEvent, ...filteredQueue]);
  }

  private onCloseListener: VoidFunction | undefined = undefined;
  private setOnCloseListener(onClose: VoidFunction | undefined) {
    this.onCloseListener = onClose;
  }

  private suppressedTimerStartIds = new Set<string>();

  /**
   * A suppressed event never reaches the popup sequence, which is where an event that has not been
   * impressed yet gets its timer started. Its lobby widget is the impression instead — without this
   * the widget would sit there with canDisplay false and render no countdown at all.
   *
   * Once per event: the response comes back with canDisplay true, and the id guards the window
   * before that where a refetch could fire a second request.
   */
  private startTimersForSuppressedWidgets() {
    this.widgetEventsLists.forEach((event) => {
      const { userEventTriggerId } = event;

      if (
        event.canDisplay ||
        this.suppressedTimerStartIds.has(userEventTriggerId) ||
        !isAutoPopupSuppressed(userEventTriggerId)
      ) {
        return;
      }

      this.suppressedTimerStartIds.add(userEventTriggerId);
      void this.startEventTimer(event.isNewEventType ? userEventTriggerId : event.eventTrigger.eventTriggerId);
    });
  }

  public async openPopup(popup: EventTriggerPopupData, onClose?: VoidFunction, source?: PurchaseSource) {
    suppressAutoPopupIfNeeded(popup);
    this.lastOfferSource = source || 'offer';
    this.setOnCloseListener(onClose);
    this.setCurrentActiveEvent(popup);
    this.rootStore.uiStore.setOpenDialog({ dialog: EnumOpenDialog.isOpenEventTriggerDialog });
    await this.setPopupState({ newIsOpen: true });
  }

  public async closePopup(preventCloseListener?: boolean) {
    if (this.onCloseListener && !preventCloseListener) {
      this.onCloseListener();
    }
    this.setOnCloseListener(undefined);
    await this.setPopupState({ newIsOpen: false });
    this.setCurrentActiveEvent(null);
    this.rootStore.uiStore.setCloseDialog();
  }

  private updateWidgetOnStartTimer(userEventTriggerId: string, expiredAt: Date) {
    const newWidgets = this.widgetEventsLists.map((widget) => {
      if (widget.userEventTriggerId === userEventTriggerId) {
        return {
          ...widget,
          expiredAt,
          canDisplay: true,
        };
      }
      return widget;
    });

    this.updateWidgetEvents(newWidgets);
  }

  private updateWidgetEvents(widgetsEvents: EventTriggerPopupData[]) {
    const lobbyWidgets = widgetsEvents.filter(
      (event) => event.benefit.visibility?.lobby !== false && !isTakeOrLoseBenefit(event.benefit),
    );
    this.setWidgetEventsList(lobbyWidgets);

    const lobbyWidgetTimers = lobbyWidgets.reduce<Record<string, number>>((acc, event) => {
      const { rewardClaimEndDate, expiredAt } = event;
      let actualExpiredAt = expiredAt;

      if (isWithinRewardClaimWindow(expiredAt, rewardClaimEndDate) && this.shouldExtendToClaimPeriod(event)) {
        actualExpiredAt = rewardClaimEndDate;
      }

      if (!event.userEventTriggerId) {
        return acc;
      }
      return {
        ...acc,
        [event.userEventTriggerId]: dayjs(actualExpiredAt).diff(dayjs(), 'second'),
      };
    }, {});

    const activePackagesTimers = lobbyWidgets.reduce((acc, event) => {
      if (!event.userEventTriggerId || !isRollingOfferPopupType(event.benefit)) {
        return acc;
      }

      const activeBenefitPackage = event.benefit.benefitPackages?.find((pkg) => pkg.isActive);

      if (!activeBenefitPackage) {
        return acc;
      }

      return {
        ...acc,
        [activeBenefitPackage.id]: activeBenefitPackage?.timeLeft || 0,
      };
    }, {});

    if (!isEmpty(lobbyWidgetTimers)) {
      const mergedTimers = {
        ...this.timers,
        ...lobbyWidgetTimers,
      };
      this.setTimers(mergedTimers);
      this.startTimers();
      this.setActiveBenefitPackagesTimers(activePackagesTimers);
      this.startActivePackagesTimers();
    }
  }

  // packageId is the package the player picked in a multi-package gift; a single-package gift sends none.
  public async claimGift(packageId?: number) {
    let isClaimed = false;
    if (this.isActionBusy || !this.currentActiveEventPopup) return;
    this.setIsActionBusy(true);
    try {
      const { key: benefitKey } = this.currentActiveEventPopup.benefit;
      const activeEvent = {
        benefit: this.currentActiveEventPopup.benefit,
        eventTrigger: this.currentActiveEventPopup.eventTrigger,
        userEventTriggerId: this.currentActiveEventPopup.userEventTriggerId,
        isNewEventType: this.currentActiveEventPopup.isNewEventType,
      };
      await postRequest(`gift/${benefitKey}`, { activeEvent, ...(packageId !== undefined && { packageId }) });
      isClaimed = true;
    } catch (e) {
      devError('Error while claiming gift', e);
    }
    this.setIsActionBusy(false);
    return isClaimed;
  }

  public async claimPersonalGift() {
    let isClaimed = false;
    if (this.isActionBusy || !this.currentActiveEventPopup) return;
    if (!isPersonalGiftPopupType(this.currentActiveEventPopup.benefit)) {
      devError('Trying to claim personal gift, but current active event is not personal gift');
      return;
    }
    this.setIsActionBusy(true);
    try {
      if (!this.currentActiveEventPopup?.userGift) {
        return;
      }
      const { id: userGiftId } = this.currentActiveEventPopup.userGift;
      await postRequest(`gift/personal-gift/${userGiftId}`);
      isClaimed = true;
    } catch (e) {
      devError('Error while claiming gift', e);
    }
    this.setIsActionBusy(false);
    return isClaimed;
  }

  // After a claimed gift closes: hand over to its free spins if it granted any, else the next popup.
  public async continueAfterGiftClaim(bundleItems: BundleItem[]) {
    if (await this.handOverToGrantedFreeSpins(bundleItems)) {
      return;
    }
    await this.showNextPopup();
  }

  // A claimed gift's free spins are played from the free spins dialog, which otherwise opens only at
  // its own slot in the lobby order — usually already passed or far behind, so the player saw it on
  // the next lobby visit at best. Opens it right after the gift instead (the app does the same) and
  // resumes the gift popups after it. Mini-game spins are skipped: the claim animation launches
  // those itself.
  private async handOverToGrantedFreeSpins(bundleItems: BundleItem[]): Promise<boolean> {
    // Only on the gift popups' own turn: handing over ends the feature on screen.
    const { featureManagerStore, userFreeSpinsStore } = this.rootStore;
    const currentFeatureType = featureManagerStore.getCurrentFeatureType();
    if (!currentFeatureType || !this.dialogTypes.includes(currentFeatureType)) {
      return false;
    }

    if (!(await userFreeSpinsStore.queueGrantedDialog(bundleItems))) {
      return false;
    }

    return featureManagerStore.showNextThenResume('UserFreeSpins');
  }

  // Not behind isActionBusy: a still-pending use of an earlier message must not swallow this one.
  public async notifyUserMessage(event: EventTriggerPopupData): Promise<boolean | undefined> {
    try {
      const { benefit, eventTrigger, userEventTriggerId, isNewEventType } = event;
      const activeEvent = { benefit, eventTrigger, userEventTriggerId, isNewEventType };

      const response = await postRequest<{ data: { isLastUsage: boolean } }>(`message/${benefit.key}`, { activeEvent });
      return response.data.isLastUsage;
    } catch (e) {
      devError('Error while notifying message', e);
    }
  }

  public async enterRaffle(raffleId: string, event: EventTriggerPopupData): Promise<boolean> {
    const response = await postRequest<ApiResponse<EventTriggersResponse>>(`/v2/raffle/${raffleId}/enter`, {
      userEventTriggerId: event.userEventTriggerId,
      benefitKey: event.benefit.key,
    });

    const data = get(response, 'data.data', null);

    if (isEmpty(data)) {
      return false;
    }

    const dataWithEventsCompacted = this.compactEventTriggerData(data);

    const validatedEvents = reduceInvalidEventsData(dataWithEventsCompacted.currentEventsByTrigger);
    const validatedGifts = reduceInvalidPersonalGifts(dataWithEventsCompacted.personalGifts);

    const activeEventsData = this.handleActiveEventsPopups({
      ...dataWithEventsCompacted,
      currentEventsByTrigger: validatedEvents,
      personalGifts: validatedGifts,
    });
    this.setActiveEventPopups(activeEventsData, { removeRaffleIds: [raffleId] });

    const nextEvent =
      validatedEvents.find((e) => e.userEventTriggerId !== event.userEventTriggerId) ?? validatedGifts[0];

    if (nextEvent) {
      this.sendAnalyticsEvents({ event_action: 'impression', triggered_by: 'click' }, nextEvent);
      await this.openPopup(nextEvent);
      this.shownEventIds.add(nextEvent.userEventTriggerId);

      this.setActiveEventsPopupsQueue(
        this.activeEventsPopupsQueue.filter((e) => e.userEventTriggerId !== nextEvent.userEventTriggerId),
      );
      return true;
    }

    return false;
  }

  public async showNextPopup(): Promise<boolean> {
    if (this.isBusy) return false;
    this.setIsBusy(true);

    if (this.isPopupOpen) {
      await this.closePopup();
    }

    let nextPopup = this.getNextPopup();

    if (nextPopup) {
      if (this.isPopupOpen) {
        await sleep(300);
      }

      if (!nextPopup.canDisplay) {
        const popupId = nextPopup.isNewEventType ? nextPopup.userEventTriggerId : nextPopup.eventTrigger.eventTriggerId;
        const updatedPopup = await this.startEventTimer(popupId);

        if (updatedPopup) {
          nextPopup = updatedPopup;
        } else {
          // if timer is not started, something went wrong, so we should remove this popup from the queue
          return this.showNextPopup();
        }
      }

      const triggered_by = this.fetchCount === 1 ? 'login' : 'lobby';
      this.sendAnalyticsEvents(
        {
          event_action: 'impression',
          triggered_by,
        },
        nextPopup,
      );

      await this.openPopup(nextPopup);

      // Mark this event as shown to prevent re-triggering from socket events
      this.shownEventIds.add(nextPopup.userEventTriggerId);

      if (isPersonalGiftPopupType(nextPopup.benefit)) {
        this.setActiveEventsPopupsQueue(
          this.activeEventsPopupsQueue.filter((event) => event?.userGift?.id !== nextPopup?.userGift?.id),
        );
      } else {
        this.setActiveEventsPopupsQueue(
          this.activeEventsPopupsQueue.filter((event) => event.userEventTriggerId !== nextPopup?.userEventTriggerId),
        );
      }

      this.setIsBusy(false);
      return true;
    }
    this.setIsBusy(false);
    this.rootStore.featureManagerStore.onDialogFinished('EventTriggerGifts');
    return false;
  }

  private async preloadImages(images: string[]) {
    const promises = images.map((src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
      });
    });
    await Promise.all(promises);
  }

  private prepareEventsRiveFiles(events: EventTriggerPopupData[]) {
    const urls: string[] = [];
    for (const event of events) {
      const { riveLayout } = event.benefit;
      if ('riveFileUrl' in event.benefit && event.benefit.riveFileUrl) urls.push(event.benefit.riveFileUrl);
      if (riveLayout) urls.push(riveLayout);
      this.prepareActionEventRiveFiles(event, urls);
    }
    urls.forEach((url) => this.rootStore.riveFileStore.addFileToPreload(url));
  }

  private prepareActionEventRiveFiles(event: EventTriggerPopupData, urls: string[]) {
    if (event.benefit.benefitType === 'action') {
      const actionType = event?.eventTrigger?.extra?.actionType;
      if (actionType === EVENT_TRIGGER_ACTION_TYPE.COIN_TUTORIAL) {
        urls.push('/assets/rive/ccsc_toggle.riv');
      }
    }
  }

  private async prepareEventsImages(events: EventTriggerPopupData[]) {
    const images = events.reduce<string[]>((acc, event) => {
      if (typeof event.benefit.imageUrl === 'string') {
        acc.push(event.benefit.imageUrl);
      }
      return acc;
    }, []);

    if (!isEmpty(images)) {
      await this.preloadImages(images);
    }
  }

  private async onJoinedNewTournament() {
    await this.rootStore.featureManagerStore.fetchAndStartDialogs(['Tournament'], { force: true });
  }

  private async onAssignedStampCard() {
    await this.rootStore.featureManagerStore.fetchAndStartDialogs(['StampCard']);
  }

  private async prepareEventAssets(events: EventTriggerPopupData[]) {
    await Promise.allSettled([this.prepareEventsImages(events), this.prepareEventsRiveFiles(events)]);
  }

  private setActiveEventPopups(eventsData: EventTriggersResponse, options: { removeRaffleIds?: string[] } = {}) {
    try {
      this.setLastEventTriggerResponse(eventsData);

      const { currentEventsByTrigger, otherActiveEvents, personalGifts, joinedNewTournament, assignedStampCard } =
        eventsData;

      const validatedEventsByTrigger = reduceInvalidEventsData(currentEventsByTrigger);
      const validatedOtherActiveEvents = reduceInvalidEventsData(otherActiveEvents);
      const validatedPersonalGifts = reduceInvalidPersonalGifts(personalGifts);

      if (joinedNewTournament) {
        void this.onJoinedNewTournament();
      }

      if (assignedStampCard) {
        void this.onAssignedStampCard();
      }

      void this.prepareEventAssets([
        ...validatedOtherActiveEvents,
        ...validatedEventsByTrigger,
        ...validatedPersonalGifts,
      ]);

      const removeRaffleSet = new Set(options.removeRaffleIds ?? []);
      const isRenderable = (event: EventTriggerPopupData) =>
        !isQuestEventTrigger(event.benefit) &&
        !isActionEventTrigger(event.benefit) &&
        !(removeRaffleSet.size > 0 && isRaffleBenefit(event.benefit) && removeRaffleSet.has(event.benefit.raffleId));

      const newActiveEventsQueue = uniqBy(
        [...validatedEventsByTrigger, ...validatedPersonalGifts, ...this.activeEventsPopupsQueue],
        'userEventTriggerId',
      );

      const filteredEvents = newActiveEventsQueue
        .filter(isRenderable)
        .filter((event) => !this.shownEventIds.has(event.userEventTriggerId));
      // Response entries come first so fresh state wins on userEventTriggerId collisions.
      const nextWidgetEvents = uniqBy(
        [...validatedOtherActiveEvents, ...validatedEventsByTrigger, ...this.widgetEventsLists],
        'userEventTriggerId',
      ).filter(isRenderable);
      const questEvent = newActiveEventsQueue.find((event) => isQuestEventTrigger(event.benefit));
      const actionEvents = validatedEventsByTrigger.filter((event) => isActionEventTrigger(event.benefit));

      if (questEvent && !this.rootStore.questStore.currentPlayerQuest) {
        this.rootStore.questStore.setCurrentPlayerQuest(questEvent.benefit);
      }

      this.setActiveEventsPopupsQueue(filteredEvents);
      this.updateWidgetEvents(nextWidgetEvents);
      this.startTimersForSuppressedWidgets();
      this.updateStoreOffers(nextWidgetEvents);
      this.rootStore.actionEventTriggerStore.registerEvents(actionEvents);

      const currentPopupId = this.currentActiveEventPopup?.userEventTriggerId;
      if (currentPopupId) {
        const refreshed = nextWidgetEvents.find((e) => e.userEventTriggerId === currentPopupId);
        if (refreshed && refreshed !== this.currentActiveEventPopup) {
          this.setCurrentActiveEvent(refreshed);
        }
      }
    } catch (e) {
      const errorMessage = getErrorMessage(e);
      void logToNewRelic('setActiveEventPopups', errorMessage, 'error');
    }
  }

  public sendAnalyticsEvents(
    args: { event_action: EventAction; extraArgs?: any } & WidgetEventOptionalArgs &
      Pick<CommonEventTriggerEventProps, 'package_id'>,
    eventData?: EventTriggerPopupData,
  ) {
    try {
      const {
        event_action,
        action_result,
        button_text,
        click_location,
        triggered_by,
        event_value,
        extraArgs,
        package_id,
      } = args;
      // Kept whole, not just destructured: a limited offer's stock is read from the event, not the benefit.
      const event = eventData || this.currentActiveEventPopup;
      const { eventTrigger, benefit } = event || {};
      const event_trigger_id = eventTrigger?.eventTriggerId;

      if (!benefit) return;

      let packageData = {};

      if (isOfferPopupType(benefit) || isPersonalGiftPopupType(benefit) || isGiftPopupType(benefit)) {
        packageData = {
          gc_coin: benefit.gcCoin,
          sc_coin: benefit.scCoin,
          package_id: benefit?.packageId,
        };
      } else if (isQuestEventTrigger(benefit)) {
        const milestonesCompleted = benefit.userQuest.userQuestMilestones.filter(
          (milestone) => !!milestone.completedAt,
        ).length;

        packageData = {
          milestones_completed: milestonesCompleted,
          num_of_milestones: benefit.userQuest.userQuestMilestones.length,
          package_id: benefit.rewardPackage?.packageId,
          quest_ordered: benefit.ordered,
        };
      } else if (isOfferV2PopupType(benefit)) {
        const riveAnimationEnable = resolveFlag('enable_offer_rive_animation', true);
        const limitedOfferStock = getPlayerLimitedOfferStock(event);

        packageData = {
          is_rive: riveAnimationEnable && !!benefit.riveFileUrl,
          offer_mode: benefit.mode,
          ...(limitedOfferStock ? { stock_left: limitedOfferStock.stock } : {}),
        };
      }

      let feature_sub_type: EventFeatureSubType | null = null;
      let feature_order = 0;
      const { EventTriggerOffers, EventTriggerMessages, EventTriggerGifts, PersonalGift, EventTriggerRoad } =
        this.rootStore.featureManagerStore.dialogManagerData || {};
      switch (benefit.benefitType) {
        case 'offer': {
          feature_order = EventTriggerOffers?.order || 0;
          feature_sub_type = 'event_trigger_offers';
          break;
        }
        case 'gift':
        case 'multiPackageGift': {
          feature_order = EventTriggerGifts?.order || 0;
          feature_sub_type = 'event_trigger_gifts';
          break;
        }
        case 'message': {
          feature_order = EventTriggerMessages?.order || 0;
          feature_sub_type = 'event_trigger_messages';
          break;
        }
        case 'personalGift': {
          feature_order = PersonalGift?.order || 0;
          feature_sub_type = 'personal_gift';
          break;
        }
        case 'road': {
          feature_order = EventTriggerRoad?.order || 0;
          feature_sub_type = 'event_trigger_crown_road';
          break;
        }
        default: {
          feature_sub_type = 'event_trigger_offers';
        }
      }

      const raffleData = isRaffleBenefit(benefit)
        ? {
            raffle_id: benefit.raffleId,
            ...(benefit.raffleStage
              ? {
                  event_category: 'raffle' as const,
                  event_name: RAFFLE_STAGE_TO_EVENT_NAME[benefit.raffleStage],
                }
              : {}),
          }
        : {};

      // The package the player picked in a multi-package gift.
      const chosenPackageData: Pick<CommonEventTriggerEventProps, 'package_id'> =
        package_id === undefined ? {} : { package_id };

      void analytics.sendEvent('widget', {
        event_action,
        feature_type: 'event_trigger',
        feature_sub_type,
        feature_order,
        triggered_by,
        action_result,
        button_text,
        click_location,
        event_value,
        ...extraArgs,
        event_trigger_id,
        ...getBenefitTypeAnalyticsProps(benefit.benefitType),
        ...getGiftPackageCountAnalyticsProps(benefit),
        benefit_key: benefit.key,
        current_step: eventData?.currentStep || 1,
        ...packageData,
        ...chosenPackageData,
        ...raffleData,
      });
    } catch (e) {
      devError('EventTriggerStore.sendAnalyticsEvents', e);
    }
  }

  private subscribeToEventsReceivedSocket(queueData: { data: EventTriggersResponse }) {
    // Check for new events BEFORE updating the state
    const incomingEventIds = [
      ...queueData.data.currentEventsByTrigger,
      ...queueData.data.otherActiveEvents,
      ...queueData.data.personalGifts,
    ].map((event) => event.userEventTriggerId);

    const hasNewEvents = incomingEventIds.some((id) => !this.shownEventIds.has(id));

    this.setActiveEventPopups(queueData.data);

    // Only trigger dialog sequence if there are genuinely new events
    if (hasNewEvents) {
      // Status may still be stale (e.g. 'ERROR'/'OFF' from a fetch with no active events at login),
      // which would make the sequence skip these dialog types without showing anything.
      this.dialogTypes.forEach((dialogType) =>
        this.rootStore.featureManagerStore.updateFeatureStatus(dialogType, 'READY'),
      );
      this.rootStore.featureManagerStore.addDialogsToSequenceQueue(this.dialogTypes, true);
    }
  }

  private getDialogTypesByEventType(type: EventTriggerPopupType): FeatureManagerDialogType[] {
    switch (type) {
      case 'gift':
        return ['EventTriggerGifts'];
      case 'message':
        return ['EventTriggerMessages'];
      case 'offer':
      case '2-3-offer':
        return ['EventTriggerOffersV2'];
      case 'personalGift':
        return ['PersonalGift'];
      case 'rollingOffer':
        return ['EventTriggerRollingOffers'];
      case 'road':
        return ['EventTriggerRoad'];
      default:
        return ['EventTriggerGifts']; // default to EventTriggerGifts if type is not recognized (tournament / booster / etc)
    }
  }

  private subscribeToEventStartedSocket(eventStartedData: { eventTriggerId: string; type: EventTriggerPopupType }) {
    const { eventTriggerId, type } = eventStartedData;
    const event = [...this.widgetEventsLists, ...this.activeEventsPopupsQueue].find(
      (activeEvent) => activeEvent.eventTrigger.eventTriggerId === eventTriggerId,
    );

    if (event) {
      devLog(':subscribeToEventStartedSocket: event already in user events');
      return;
    }

    const eventInStoreOffers = this.storeOffers.find((storeOffer) => storeOffer.eventTriggerId === eventTriggerId);

    if (eventInStoreOffers) {
      devLog(':subscribeToEventStartedSocket: event already in store offers');
      return;
    }

    // 5 seconds to 5 minutes
    const timeoutMs = 5000 + Math.random() * (5 * 60 * 1000 - 5000);

    const dialogTypes = this.getDialogTypesByEventType(type);

    setTimeout(() => {
      void this.rootStore.featureManagerStore.fetchAndStartDialogs(dialogTypes);
    }, timeoutMs);
  }

  private get isGrantedExtraOfferOpenable(): boolean {
    // animationBlockScreenVisible covers the new rewards animation too, which never sets
    // CoinsAnimationVisible — without it the offer opened mid-claim, over the coins.
    return (
      !this.rootStore.uiStore.CoinsAnimationVisible &&
      !this.rootStore.animationStore.animationBlockScreenVisible &&
      !this.isPopupOpen &&
      !this.isBusy
    );
  }

  private clearGrantedExtraOffer(): void {
    this.grantedExtraOfferTriggerId = '';
    this.grantedExtraOfferArmedAt = 0;
  }

  private hasGrantedExtraOfferExpired(): boolean {
    return Date.now() - this.grantedExtraOfferArmedAt > GRANTED_EXTRA_OFFER_OPEN_WINDOW_MS;
  }

  private async openGrantedExtraOfferIfReady(): Promise<void> {
    const eventTriggerId = this.grantedExtraOfferTriggerId;
    if (!eventTriggerId) return;

    // Inside a slot the in-slot claim already flew the reward to the home icon; popping the offer
    // over the game would interrupt play. The offer stays queued, and the feature manager (which
    // holds dialogs while a game is open) pops it once the player is back in the lobby.
    if (this.rootStore.gameStore.gameId) {
      this.clearGrantedExtraOffer();
      return;
    }

    if (this.hasGrantedExtraOfferExpired()) {
      this.clearGrantedExtraOffer();
      return;
    }

    if (!this.isGrantedExtraOfferOpenable) return;

    const isReadyToOpen = this.widgetEventsLists.some((event) => event.eventTrigger.eventTriggerId === eventTriggerId);
    if (!isReadyToOpen) return;

    this.clearGrantedExtraOffer();
    this.openedGrantedTriggerIds.add(eventTriggerId);
    this.socketOpenedGrantAt.set(eventTriggerId, Date.now());
    await this.openPopupByEventTriggerId(eventTriggerId);
  }

  // Prize Drop % Extra: game-ui knows the claimed offer's benefit key, not the event trigger id monetization
  // creates when granting it — so look the granted event up by key, then reuse the open_monetization_event path.
  public async openGrantedOfferByBenefitKey(benefitKey: string): Promise<void> {
    // Each crate grants its own instance of the same offer. Only the latest claim for a key opens anything: a newer
    // claim supersedes an older lookup still waiting, so overlapping claims can't end on an older grant.
    const token = ++this.benefitKeyLookupSeq;
    this.latestBenefitKeyLookup.set(benefitKey, token);
    await this.lookUpGrantedOffer(benefitKey, token, 1);
  }

  private async lookUpGrantedOffer(benefitKey: string, token: number, attempt: number): Promise<void> {
    const isSuperseded = () => this.latestBenefitKeyLookup.get(benefitKey) !== token;
    await this.rootStore.featureManagerStore.fetchAndStartDialogs(this.dialogTypes, { force: true });
    if (isSuperseded()) return;

    const grants = this.widgetEventsLists.filter((e) => (e.benefit?.key ?? e.benefitKey) === benefitKey);
    const isOpened = (e: EventTriggerPopupData) => this.openedGrantedTriggerIds.has(e.eventTrigger.eventTriggerId);
    // Newest by start date; on a tie (or no dates) an unopened grant wins, otherwise list order is kept.
    const newest = grants.reduce<EventTriggerPopupData | undefined>((latest, e) => {
      if (!latest) return e;
      const diff = startTime(e) - startTime(latest);
      return diff > 0 || (diff === 0 && isOpened(latest) && !isOpened(e)) ? e : latest;
    }, undefined);
    const newestId = newest?.eventTrigger.eventTriggerId;
    if (newestId && !this.openedGrantedTriggerIds.has(newestId)) {
      // Older grants of the same offer are superseded by the latest one and must never open over it.
      grants.forEach((e) => this.openedGrantedTriggerIds.add(e.eventTrigger.eventTriggerId));
      if (this.grantedExtraOfferTriggerId === newestId) this.clearGrantedExtraOffer();
      // The open may wait on the offer's timer; a newer claim that finds this same grant leaves it to finish, and one
      // that opens a newer grant takes over, which cancels this open after the timer.
      this.openingGrantByKey.set(benefitKey, newestId);
      try {
        await this.openPopupByEventTriggerId(
          newestId,
          undefined,
          () => this.openingGrantByKey.get(benefitKey) === newestId,
        );
      } finally {
        if (this.openingGrantByKey.get(benefitKey) === newestId) this.openingGrantByKey.delete(benefitKey);
      }
      return;
    }
    if (newestId && this.openingGrantByKey.get(benefitKey) === newestId) return;
    // Lobby only: inside a slot the socket never opens offers, so a recent socket open there belongs to an earlier claim.
    const socketOpenedAt = newestId ? this.socketOpenedGrantAt.get(newestId) : undefined;
    const handledBySocket =
      !this.rootStore.gameStore.gameId &&
      socketOpenedAt !== undefined &&
      Date.now() - socketOpenedAt < GRANTED_OFFER_SOCKET_HANDLED_MS;
    if (handledBySocket) return;

    // The newest grant is one opened earlier (a previous crate): wait for this claim's grant to arrive.
    if (attempt >= GRANTED_OFFER_LOOKUP_ATTEMPTS) {
      devError('openGrantedOfferByBenefitKey: granted offer not found in events', { benefitKey });
      return;
    }
    await sleep(GRANTED_OFFER_LOOKUP_DELAY_MS);
    if (isSuperseded()) return;
    await this.lookUpGrantedOffer(benefitKey, token, attempt + 1);
  }

  private async subscribeToClaimPackage(message?: ClaimPackageCompletedEnvelope): Promise<void> {
    const eventTriggerId = message?.data?.eventTriggerId;

    await this.rootStore.featureManagerStore.fetchAndStartDialogs(
      this.dialogTypes,
      eventTriggerId ? { force: true } : undefined,
    );

    if (!eventTriggerId || this.openedGrantedTriggerIds.has(eventTriggerId)) return;

    this.grantedExtraOfferTriggerId = eventTriggerId;
    this.grantedExtraOfferArmedAt = Date.now();

    await this.openGrantedExtraOfferIfReady();
  }
}
